<template>
  <q-footer class="footer-quasar bg-dark text-white q-py-md" style="width: 100%; position: static !important;">
    <div class="q-container q-pa-sm q-mb-none">
      <!-- Desktop Footer: Compact Version -->
      <div v-if="!$q.platform.is.mobile" class="row items-center justify-between no-wrap">
        <!-- About (short) -->
        <div class="col-auto flex items-center">
          <span class="text-weight-bold">CouponLa</span>
          <span class="q-ml-sm text-caption">Save with verified coupons & deals</span>
        </div>
        <!-- Horizontal Links -->
        <nav class="col-auto flex items-center" aria-label="Footer links">
          <q-btn flat dense no-caps size="sm" to="/" label="Home" class="q-mx-xs" />
          <q-btn flat dense no-caps size="sm" to="/stores" label="Stores" class="q-mx-xs" />
          <q-btn flat dense no-caps size="sm" to="/categories" label="Categories" class="q-mx-xs" />
          <q-btn flat dense no-caps size="sm" to="/about" label="About" class="q-mx-xs" />
          <q-btn flat dense no-caps size="sm" to="/privacy-policy" label="Privacy Policy" class="q-mx-xs" />
          <q-btn flat dense no-caps size="sm" to="/terms-of-service" label="Terms of Service" class="q-mx-xs" />
          <q-btn flat dense no-caps size="sm" to="/contact-us" label="Contact" class="q-mx-xs" />
        </nav>
        <!-- Social Icons (smaller) -->
        <nav aria-label="Social links" class="col-auto flex items-center">
          <q-btn flat round size="sm" color="white" icon="fab fa-facebook" href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" class="q-mx-xs" />
          <q-btn flat round size="sm" color="white" icon="fab fa-twitter" href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" class="q-mx-xs" />
          <q-btn flat round size="sm" color="white" icon="fab fa-instagram" href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" class="q-mx-xs" />
        </nav>
      </div>
      <!-- Mobile Footer -->
      <div v-else>
        <!-- Mobile navigation bar (fixed at bottom) -->
        <div class="footer-mobile q-pt-md q-pb-sm">
          <div class="row justify-around items-center q-mb-md">
            <q-btn flat dense icon="home" label="Home" to="/" no-caps class="footer-mobile-btn" />
            <q-btn flat dense icon="storefront" label="Stores" to="/stores" no-caps class="footer-mobile-btn" />
            <q-btn flat dense icon="grid_view" label="Categories" to="/categories" no-caps class="footer-mobile-btn" />
            <q-btn flat dense icon="local_offer" label="Coupons" to="/coupons" no-caps class="footer-mobile-btn" />
            <q-btn flat dense icon="more_horiz" @click="showMobileMenu = true" no-caps class="footer-mobile-btn" />
          </div>
        </div>
        
        <!-- Actual footer content for mobile (not fixed) -->
        <div class="q-my-md">
          <!-- Newsletter section -->
          <div class="q-px-md q-py-md">
            <h5 class="q-mt-none q-mb-sm text-weight-bold">Newsletter</h5>
            <p class="q-mb-md text-body2">Subscribe to get the latest deals directly in your inbox.</p>
            <q-form @submit="subscribeNewsletter" class="newsletter-form">
              <div class="row q-col-gutter-sm">
                <div class="col">
                  <q-input
                    v-model="email"
                    outlined
                    dense
                    type="email"
                    placeholder="Your email"
                    required
                    bg-color="dark"
                    aria-label="Email address"
                    dark
                  />
                </div>
                <div class="col-auto">
                  <q-btn
                    :loading="subscribing"
                    type="submit"
                    color="amber"
                    text-color="black"
                    label="Subscribe"
                    no-caps
                    unelevated
                  />
                </div>
              </div>
              <div v-if="subscribeSuccess" class="text-positive text-caption q-mt-sm">
                <q-icon name="check_circle" size="xs" class="q-mr-xs" />Thank you for subscribing!
              </div>
              <div v-if="subscribeError" class="text-negative text-caption q-mt-sm">
                <q-icon name="error" size="xs" class="q-mr-xs" />{{ subscribeError }}
              </div>
            </q-form>
          </div>
          
          <!-- Social icons -->
          <div class="social-icons q-my-lg text-center">
            <q-btn flat round color="white" icon="fab fa-facebook" href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" class="q-mx-xs" />
            <q-btn flat round color="white" icon="fab fa-twitter" href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" class="q-mx-xs" />
            <q-btn flat round color="white" icon="fab fa-instagram" href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" class="q-mx-xs" />
            <q-btn flat round color="white" icon="fab fa-pinterest" href="https://pinterest.com" target="_blank" rel="noopener noreferrer" aria-label="Pinterest" class="q-mx-xs" />
          </div>
          
          <div class="text-center text-caption text-grey-5 q-mb-xs">
            &copy; {{ currentYear }} CouponLa. All Rights Reserved.
          </div>
          <div class="text-center q-mb-md">
            <q-btn to="/privacy-policy" flat dense no-caps label="Privacy Policy" size="sm" color="grey-5" class="q-mx-xs" />
            <q-btn to="/terms-of-service" flat dense no-caps label="Terms of Service" size="sm" color="grey-5" class="q-mx-xs" />
            <q-btn to="/contact-us" flat dense no-caps label="Contact" size="sm" color="grey-5" class="q-mx-xs" />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer bottom section (desktop only — mobile already has its own) -->
    <template v-if="!$q.platform.is.mobile">
      <q-separator color="grey-7" class="q-my-md" />
      <div class="row items-center">
        <div class="col-12 col-md-6 text-center text-md-left q-mb-sm q-mb-md-none">
          <div class="text-caption text-grey-5">
            &copy; {{ currentYear }} CouponLa. All Rights Reserved.
          </div>
        </div>
        <div class="col-12 col-md-6 text-center text-md-right">
          <div class="footer-bottom-links">
            <q-btn to="/privacy-policy" flat dense no-caps label="Privacy Policy" size="sm" color="grey-5" padding="xs" class="q-mr-sm" />
            <q-btn to="/terms-of-service" flat dense no-caps label="Terms of Service" size="sm" color="grey-5" padding="xs" class="q-mr-sm" />
            <q-btn to="/contact-us" flat dense no-caps label="Contact Us" size="sm" color="grey-5" padding="xs" />
          </div>
        </div>
      </div>
    </template>
    
    <!-- Back to top button -->
    <q-page-sticky position="bottom-right" :offset="[18, 18]" v-if="showBackToTop">
      <q-btn
        round
        color="primary"
        icon="arrow_upward"
        size="md"
        @click="scrollToTop"
      />
    </q-page-sticky>
    
    <!-- Mobile Menu Dialog -->
    <q-dialog 
      v-model="showMobileMenu" 
      position="bottom"
      :maximized="false"
      transition-show="slide-up"
      transition-hide="slide-down"
    >
      <q-card class="mobile-menu-card">
        <q-card-section class="q-pt-none">
          <div class="text-center q-pt-sm">
            <div class="text-h6 q-py-sm">Menu</div>
            <q-separator class="q-mb-md" />
          </div>
          
          <q-list padding>
            <q-item clickable v-close-popup to="/about">
              <q-item-section avatar>
                <q-icon name="info" color="primary" />
              </q-item-section>
              <q-item-section>About Us</q-item-section>
            </q-item>
            
            <q-item clickable v-close-popup to="/contact-us">
              <q-item-section avatar>
                <q-icon name="mail" color="primary" />
              </q-item-section>
              <q-item-section>Contact Us</q-item-section>
            </q-item>
            
            <q-item clickable v-close-popup to="/featured">
              <q-item-section avatar>
                <q-icon name="star" color="amber" />
              </q-item-section>
              <q-item-section>Featured Deals</q-item-section>
            </q-item>
            
            <q-item clickable v-close-popup to="/trending">
              <q-item-section avatar>
                <q-icon name="trending_up" color="red" />
              </q-item-section>
              <q-item-section>Trending Offers</q-item-section>
            </q-item>
            
            <q-separator class="q-my-md" />
            
            <q-item-label header>Top Categories</q-item-label>
            
            <q-item 
              v-for="category in categories" 
              :key="category.id" 
              clickable 
              v-close-popup
              :to="{ name: 'Category', params: { slug: category.url_slug } }"
            >
              <q-item-section avatar>
                <q-icon name="category" color="primary" />
              </q-item-section>
              <q-item-section>{{ category.name }}</q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        
        <q-card-actions align="center">
          <q-btn flat color="primary" label="Close" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-footer>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useQuasar } from 'quasar';
import { useRoute } from 'vue-router';
import { categoriesService, newsletterService } from '@/services/supabase';

// Quasar helper
const $q = useQuasar();
const route = useRoute();

// Reactive data
const email = ref('');
const subscribing = ref(false);
const subscribeSuccess = ref(false);
const subscribeError = ref(null);
const categories = ref([]);
const showBackToTop = ref(false);
const showMobileMenu = ref(false);
const activeRoute = ref('');

// Watch route changes to update active route
watch(() => route.path, (newPath) => {
  activeRoute.value = newPath;
}, { immediate: true });

// Computed properties
const currentYear = computed(() => new Date().getFullYear());

// Methods
const subscribeNewsletter = async () => {
  if (!email.value) return;
  
  subscribing.value = true;
  subscribeError.value = null;
  
  try {
    await newsletterService.subscribe(email.value);
    
    subscribeSuccess.value = true;
    email.value = '';
    
    $q.notify({
      message: 'Successfully subscribed to newsletter',
      color: 'positive',
      position: $q.platform.is.mobile ? 'bottom' : 'top',
      timeout: 2000
    });
  } catch (error) {
    console.error('Newsletter subscription error:', error);
    subscribeError.value = 'Failed to subscribe. Please try again later.';
  } finally {
    subscribing.value = false;
  }
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

const checkScroll = () => {
  showBackToTop.value = window.scrollY > 500;
};

// Load top categories
const loadCategories = async () => {
  try {
    const { data } = await categoriesService.getTopCategories(5);
    if (data) {
      categories.value = data;
    }
  } catch (error) {
    console.error('Error loading top categories:', error);
  }
};

// Check if we need to add bottom padding to the main content
const adjustMainContentPadding = () => {
  if ($q.platform.is.mobile) {
    const mainContent = document.querySelector('.q-page-container');
    if (mainContent) {
      // Add padding at the bottom to account for the fixed footer
      mainContent.style.paddingBottom = '70px';
    }
  }
};

// Load data on component mount
onMounted(() => {
  loadCategories();
  window.addEventListener('scroll', checkScroll);
  checkScroll();
  adjustMainContentPadding();
  
  // Handle safe area insets for mobile devices
  if ($q.platform.is.mobile) {
    document.documentElement.classList.add('has-mobile-footer');
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', checkScroll);
  
  if ($q.platform.is.mobile) {
    const mainContent = document.querySelector('.q-page-container');
    if (mainContent) {
      mainContent.style.paddingBottom = '';
    }
    document.documentElement.classList.remove('has-mobile-footer');
  }
});
</script>

<style lang="scss" scoped>
.footer-quasar {
  border-top-left-radius: 1.5rem;
  border-top-right-radius: 1.5rem;
  width: 100% !important;
  position: static !important;
  min-height: unset !important;
  height: auto !important;
  flex: none !important;
  @media (max-width: 767px) {
    border-radius: 0;
  }
}

.footer-links {
  .q-item {
    min-height: 32px;

    &:hover {
      .q-item__section {
        color: $amber;
      }
    }
  }
}

.social-icons {
  .q-btn {
    transition: transform 0.2s ease;
    width: 44px; /* Larger touch target for mobile */
    height: 44px;

    &:hover {
      transform: translateY(-3px);
    }
  }
}

.newsletter-form {
  .q-field {
    border-radius: 8px;
  }
}

.footer-bottom-links {
  .q-btn {
    transition: color 0.2s ease;
    min-height: 36px; /* Larger touch target */

    &:hover {
      color: $amber !important;
    }
  }
}

.footer-mobile {
  background-color: $dark;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
  padding-bottom: env(safe-area-inset-bottom, 0px); /* Support for iPhone notch */
  
  .footer-mobile-btn {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    transition: background-color 0.2s ease;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 25%;
      right: 25%;
      height: 3px;
      background-color: transparent;
      transition: background-color 0.2s ease;
    }
    
    &.router-link-active {
      color: $amber;
      
      &::after {
        background-color: $amber;
      }
    }
    
    .q-btn__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-height: 48px;
      
      .q-icon {
        font-size: 22px;
        margin-bottom: 4px;
      }
      
      .q-btn__label {
        font-size: 12px;
        line-height: 1;
      }
    }
    
    /* Ripple effect adjustment for mobile */
    .q-focus-helper, .q-ripple {
      border-radius: 0;
      top: 0;
      left: 0;
      right: 0;
      bottom: 3px;
    }
  }
}

/* Special handling for dark mode */
.body--dark {
  .footer-mobile {
    box-shadow: 0 -2px 10px rgba(0,0,0,0.3);
  }
}

/* Fix for bottom spacing when footer is fixed */
:global(.has-mobile-footer) {
  .q-page-container {
    margin-bottom: var(--footer-height, 70px);
  }
}

/* Safe area insets for modern mobile devices */
@supports (padding: env(safe-area-inset-bottom)) {
  .footer-mobile {
    padding-bottom: env(safe-area-inset-bottom);
    --safe-area-bottom: env(safe-area-inset-bottom);
  }
  
  :global(.has-mobile-footer) {
    .q-page-container {
      margin-bottom: calc(var(--footer-height, 70px) + env(safe-area-inset-bottom, 0px));
    }
  }
}

/* Style the mobile menu card */
.mobile-menu-card {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding-bottom: env(safe-area-inset-bottom, 0);
  
  .q-list {
    .q-item {
      min-height: 48px; /* Larger touch target for menu items */
      
      &.active-item {
        background-color: rgba($primary, 0.1);
      }
    }
  }
  
  .q-card-actions {
    padding-bottom: env(safe-area-inset-bottom, 16px);
    
    .q-btn {
      min-width: 120px;
      min-height: 40px;
    }
  }
}

/* Responsive spacing adjustments */
@media screen and (max-width: 340px) {
  .footer-mobile {
    .footer-mobile-btn {
      .q-btn__content {
        min-height: 42px;
        
        .q-icon {
          font-size: 18px;
        }
        
        .q-btn__label {
          font-size: 10px;
        }
      }
    }
  }
}
</style>
